import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import projectpic1 from '../Assets/Images/project1.jpeg';
import projectpic2 from '../Assets/Images/project2.jpeg';
import projectpic3 from '../Assets/Images/project3.jpeg';
import projectpic4 from '../Assets/Images/project4.JPG';
import projectpic5 from '../Assets/Images/project5.jpg';
import projectpic6 from '../Assets/Images/project6.png';

const images = [
  {
    src: projectpic1,
    alt: "Production Cycle 1",
    title: "Cycle 1 — BNGAGRBN01",
    cycle: "Production Cycle 1",
    description: "3 Acres Yelakki Banana, 1 Acre G9 Banana, 1 Acre Tomato, 1 Acre Chilli.",
    learnings: "Our first cycle — where we tested our asset-light model and built the operational blueprint that all future cycles would follow.",
    gallery: '/gallery/BNGAGRBN01-gallery'
  },
  {
    src: projectpic2,
    alt: "Production Cycle 2",
    title: "Cycle 2 — MYSCHMBN02",
    cycle: "Production Cycle 2",
    description: "10 Acres Yelakki Banana, 10 Acres Chilli.",
    learnings: "Scaled operations to 20 acres. Refined our irrigation strategy and labour management through real field challenges.",
    gallery: '/gallery/MYSCHMBN02-gallery'
  },
  {
    src: projectpic3,
    alt: "Production Cycle 3",
    title: "Cycle 3 — MYSAVGBN03",
    cycle: "Production Cycle 3",
    description: "10 Acres Yelakki Banana, 5 Acres G9 Banana, 10 Acres Watermelon.",
    learnings: "Diversified crops across 25 acres. Validated multi-crop management and right-sized our farm parcels for genuine operational control.",
    gallery: '/gallery/MYSAVGBN03-gallery'
  },
  {
    src: projectpic4,
    alt: "Ongoing Operations",
    title: "MYSCHMBN04",
    cycle: "Ongoing",
    description: "23 Acres Yelakki Banana.",
    learnings: "Large-scale single-crop parcel. Applying standardized cultivation blueprint developed across earlier cycles.",
    gallery: '/gallery/MYSCHMBN04-gallery'
  },
  {
    src: projectpic5,
    alt: "Ongoing Operations",
    title: "MYSKTGBN05",
    cycle: "Ongoing",
    description: "18 Acres G9 Banana.",
    learnings: "Focused G9 cultivation with AI-based monitoring and a self-sustaining, increasingly skilled labour team.",
    gallery: '/gallery/MYSKTGBN05-gallery'
  },
  {
    src: projectpic6,
    alt: "Ongoing Operations",
    title: "MYSGDHBN06",
    cycle: "Ongoing",
    description: "19 Acres Yelakki & G9 Banana.",
    learnings: "Combined variety cultivation. Demonstrates the disciplined, market-ready production system our partners step into.",
    gallery: '/gallery/MYSGDHBN06-gallery'
  }
];

const INITIAL_COUNT = 3;

const Projects = () => {
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();
  const sectionRef = useRef(null);

  const visibleImages = showAll ? images : images.slice(0, INITIAL_COUNT);

  const handleExploreGallery = (galleryPath) => {
    navigate(galleryPath);
  };

  const handleToggle = () => {
    setShowAll((prev) => {
      const next = !prev;
      // When collapsing, scroll back so the section shows properly
      if (!next && sectionRef.current) {
        sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return next;
    });
  };

  return (
    <div
      ref={sectionRef}
      className="bg-gradient-to-b from-white via-green-50/30 to-white py-16 md:py-24 px-4 md:px-6 scroll-mt-24"
      id="projects"
    >
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl font-bold text-[rgb(2,58,16)] text-center mb-4">PROJECT PHOTOS</h2>
        <p className="text-center text-lg text-gray-700 font-serif max-w-3xl mx-auto mb-14">
          Real farms. Real production cycles. Browse through our farm projects and see the work behind every harvest.
        </p>

        {/* Alternating project cards */}
        <div className="space-y-8">
          {visibleImages.map((image, index) => (
            <div
              key={index}
              className={`flex flex-col ${
                index % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'
              } bg-[#f7f7f4] rounded-2xl overflow-hidden md:h-[380px] shadow-[0_12px_40px_-18px_rgba(2,58,16,0.3)] hover:shadow-[0_22px_55px_-20px_rgba(2,58,16,0.42)] transition-shadow duration-300`}
            >
              {/* Image side */}
              <div className="relative w-full md:w-1/2 h-64 md:h-full">
                <img src={image.src} alt={image.alt} className="w-full h-full object-cover object-center" />
                <span className="absolute top-4 left-4 bg-[rgb(2,58,16)] text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-md">
                  {image.cycle}
                </span>
              </div>

              {/* Content side */}
              <div className="w-full md:w-1/2 p-6 md:p-10 flex flex-col justify-center">
                <h3 className="text-2xl md:text-3xl font-bold text-[rgb(2,58,16)] mb-3">{image.title}</h3>
                <p className="text-gray-700 font-serif mb-3">{image.description}</p>
                <p className="text-gray-500 font-serif italic mb-6 leading-relaxed">{image.learnings}</p>
                <button
                  onClick={() => handleExploreGallery(image.gallery)}
                  className="self-start bg-[rgb(2,58,16)] hover:bg-green-800 text-white font-semibold px-6 py-3 rounded-full shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                >
                  View Farm Gallery
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Load More / Show Less */}
        {images.length > INITIAL_COUNT && (
          <div className="flex justify-center mt-10">
            <button
              onClick={handleToggle}
              className="group inline-flex items-center gap-2 px-8 py-3 rounded-full bg-gradient-to-r from-[rgb(2,58,16)] to-green-700 text-white font-semibold shadow-[0_10px_30px_-8px_rgba(2,58,16,0.6)] hover:shadow-[0_16px_40px_-8px_rgba(2,58,16,0.75)] hover:-translate-y-0.5 transition-all duration-300"
            >
              {showAll ? 'Show Less' : `Load More Projects (${images.length - INITIAL_COUNT})`}
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`w-4 h-4 transition-transform duration-300 ${
                  showAll ? 'rotate-180' : 'group-hover:translate-y-0.5'
                }`}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;
